import React, { useContext, useReducer, useEffect, useState } from 'react';
import { useQuery } from 'react-apollo-hooks';
import { useTranslation } from 'react-i18next';
import { loader } from 'graphql.macro';
import cn from 'classnames';
import debounce from 'debounce';

import { suppressError, ProfileContext } from '@loyalup/core';
import { Input, WithTitle, PageSize, Table } from '@loyalup/ui';

import { getFeedbacksFields } from 'components/FeedbacksFields';
import Filter from 'components/Filter';
import Filters from 'components/Filters';
import ColumnVisibility from 'components/ColumnVisibility';

import { ReactComponent as SearchIcon } from 'icons/search.svg';
import updateProfileInCache from 'utils/updateProfileInCache';

import s from './Feedbacks.module.css';

const FEEDBACKS_QUERY = loader('../../gql/queries/Feedbacks.gql');

const Feedbacks = () => {
  const [t] = useTranslation();
  const { feedbacksTableSettings } = useContext(ProfileContext);

  const { pageSize, orderBy, orderDirection, visibleColumns } = feedbacksTableSettings || {};

  const [defaultTableConfig] = useState({ offset: 0, limit: pageSize, orderBy, orderDirection });

  const { loading, data, fetchMore } = useQuery(FEEDBACKS_QUERY, {
    suspend: false,
    variables: defaultTableConfig,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const reducer = (state, action) => {
    const noSelection = { include: [], exclude: [] };
    switch (action.type) {
      case 'setTableConfig':
        return { ...state, tableConfig: action.config };
      case 'setSelection':
        return { ...state, selection: action.selection };
      case 'setFilter':
        return { ...state, filter: action.filter };
      case 'setLimit':
        return { ...state, tableConfig: { ...state.tableConfig, limit: action.limit } };
      case 'setColumns':
        return { ...state, columns: action.columns };
      case 'search':
        return {
          ...state,
          text: action.text,
          selection: { ...state.selection, ...noSelection, text: action.text },
          tableConfig: { ...state.tableConfig, offset: 0 },
        };
      case 'setTempFilter':
        return {
          ...state,
          tempFilterId: action.tempFilterId,
          selection: { ...state.selection, ...noSelection, conds: action.conds },
          tableConfig: { ...state.tableConfig, offset: 0 },
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    filter: undefined,
    tempFilterId: undefined,
    text: undefined,
    columns: visibleColumns,
    tableConfig: defaultTableConfig,
    selection: { include: [], exclude: [], text: undefined, conds: undefined },
  });

  const feedbacksQueryVars = {
    filterId: state.tempFilterId,
    text: state.text,
    ...state.tableConfig,
  };

  const loadFeedbacks = async () => {
    try {
      await fetchMore({
        variables: feedbacksQueryVars,
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return fetchMoreResult;
        },
      });
    } catch (e) {
      suppressError(e, 'Invariant Violation: 15');
    }
  };

  useEffect(() => {
    loadFeedbacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tableConfig]);

  const columns = getFeedbacksFields({ filter: visibleColumns });

  return (
    <WithTitle title={t('Обратная связь')}>
      <div className={s.header}>
        <h1>{t('Обратная связь')}</h1>
        <div className={s.spacer} />
        <div className={s.toolbarItem}>
          <Input
            theme="toolbar-white"
            expandWidth={300}
            leftIcon={<SearchIcon />}
            type="text"
            placeholder={t('Поиск')}
            onChange={debounce(text => dispatch({ text, type: 'search' }), 300)}
          />
        </div>
      </div>
      <div className={s.toolbar}>
        <Filters
          table="FEEDBACKS"
          theme="toolbar-white"
          className={s.toolbarItem}
          stretch
          fixedWidth={220}
          activeFilter={state.filter}
          onSelect={filter => dispatch({ filter, type: 'setFilter' })}
        />
        <div className={s.spacer} />
        <PageSize
          table="FEEDBACKS"
          className={s.toolbarItem}
          theme="toolbar-white"
          selected={state.tableConfig.limit}
          onChange={limit => dispatch({ limit, type: 'setLimit' })}
          afterSet={updateProfileInCache}
        />
        <ColumnVisibility
          table="FEEDBACKS"
          className={s.toolbarItem}
          fixedWidth={200}
          theme="toolbar-white"
          stretch
          visibleColumns={state.columns}
          onChange={columns => dispatch({ columns, type: 'setColumns' })}
        />
      </div>
      <Filter
        table="FEEDBACKS"
        className={cn(s.filter, { [s.visible]: !!state.filter })}
        filter={state.filter}
        onUpdate={filter => dispatch({ filter, type: 'setFilter' })}
        setTempFilter={(tempFilterId, conds) => dispatch({ tempFilterId, conds, type: 'setTempFilter' })}
      />
      <Table
        selectable
        selection={state.selection}
        total={data?.feedbacks?.total}
        tableConfig={state.tableConfig}
        columns={columns}
        rows={data?.feedbacks?.feedbacks || []}
        loading={loading}
        highlightText={state.tableConfig.text}
        onSelectionChange={selection => dispatch({ selection, type: 'setSelection' })}
        onChange={config => dispatch({ config, type: 'setTableConfig' })}
      />
    </WithTitle>
  );
};

export default Feedbacks;
